import jwtDecode from 'jwt-decode';

import { AddAcquisitionSource } from 'context/data/AddAcquisitionSource.mutation';
import { InitializeUser } from 'containers/Auth/data/InitializeUser.mutation';

import type { KindeContextProps } from '@kinde-oss/kinde-auth-react';
import type { UserType } from 'src/constants/graphql-enums';
import type { Client } from 'urql';

/**
 * Initializes a new Kinde user in the backend if not already initialized.
 *
 * @returns Promise resolving to a tuple:
 *   - `[needsReauth: boolean, null]` on success:
 *     - `needsReauth` is `true` if user was just initialized and needs fresh token
 *     - `needsReauth` is `false` if user was already initialized
 *   - `[null, error: Error]` on failure: Error object describing what went wrong
 */
export const initialiseUser = async ({
  user_type,
  utm,
  kinde,
  client,
}: {
  user_type: string;
  utm?: { utm_source?: string; utm_campaign?: string };
  kinde: KindeContextProps;
  client: Client;
}): Promise<[boolean, null] | [null, Error]> => {
  try {
    const token = await kinde.getToken();

    if (!token) {
      return [null, new Error('No token found')];
    }

    let decodedToken: {
      user_properties: {
        askable_user_id: { t?: string; v?: string };
        askable_user_type: { t?: string; v?: string };
      };
    };

    try {
      decodedToken = jwtDecode(token);
    } catch {
      return [null, new Error('Failed to decode token')];
    }

    // Check if user is already initialized
    if (decodedToken.user_properties.askable_user_id?.v && decodedToken.user_properties.askable_user_type?.v) {
      return [false, null];
    }

    // Initialize user in backend
    const result = await client
      .mutation(
        InitializeUser,
        {
          input: {
            type: user_type as UserType,
          },
        },
        {
          fetchOptions: {
            headers: {
              Authorization: token,
            },
          },
        },
      )
      .toPromise();

    if (result.error) {
      return [null, new Error(result.error.message)];
    }

    // Add acquisition source tracking, don't care for result
    void client.mutation(AddAcquisitionSource, {
      user: {
        meta: {
          acquisition: {
            ...(utm?.utm_source && { source: utm.utm_source }),
            ...(utm?.utm_campaign && { campaign: utm.utm_campaign }),
          },
        },
      },
    });

    // User was just initialized, so re-auth is needed to get fresh token with updated properties
    return [true, null];
  } catch (error) {
    return [null, error instanceof Error ? error : new Error('Unknown error during user initialization')];
  }
};
